// ISDP CUSTOM: Shared admin hero — the ONE hero every admin section renders.
// Full-bleed background photo + dark gradient overlay + eyebrow / large white
// title / intro, ported 1:1 from the app's hub banner (packages/app HubPage.css)
// so the admin area matches Catalog / Docs / Self-Service. The plugin can't
// import packages/app CSS, so the .devhub-hub__banner* rules are re-implemented
// as .devhub-adminhero* in AdminShell.css.
//
// Exported from the plugin's public index so packages/app (the System / Resource
// Monitor page) can render the exact same hero.
import type { CSSProperties, ReactNode } from 'react';
import './AdminShell.css';
import adminHero from '../../assets/admin-hero.jpg';

export interface AdminHeroProps {
  /** Large white headline (required). */
  title: string;
  /** Small uppercase cyan kicker above the title (e.g. "Admin"). */
  eyebrow?: string;
  /** Supporting paragraph under the title. */
  intro?: string;
  /**
   * Optional content rendered in a slot beneath the copy — typically an
   * <AdminTabs> strip or a row of actions. Sits on the dark hero.
   */
  children?: ReactNode;
  /**
   * Override the background image. Defaults to the shared admin hero photo.
   * Pass an imported asset URL (string) to use a section-specific image.
   */
  image?: string;
}

export const AdminHero = ({
  title,
  eyebrow,
  intro,
  children,
  image,
}: AdminHeroProps) => {
  const photoStyle: CSSProperties = {
    backgroundImage: `url(${image ?? adminHero})`,
  };

  return (
    <header className="devhub-adminhero">
      <div className="devhub-adminhero__photo" style={photoStyle} />
      <div className="devhub-adminhero__overlay" />
      <div className="devhub-adminhero__content">
        {eyebrow && <span className="devhub-adminhero__eyebrow">{eyebrow}</span>}
        <h1 className="devhub-adminhero__title">{title}</h1>
        {intro && <p className="devhub-adminhero__intro">{intro}</p>}
        {children && <div className="devhub-adminhero__slot">{children}</div>}
      </div>
    </header>
  );
};
